import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocalStorage } from 'ui/Hooks';

import { DEFAULT_INTERNAL_TOOLS_MENU } from './constants';
import type { InternalToolStorage, InternalToolsMenu } from './types';

/**
 * Provides a hook to manage the internal tools menu.
 * Internal tools are read from local storage and synced to local state.
 * When local storage changes, local state is updated.
 *
 * The hook does not update local storage unless a tool action is called; then it updates local storage with the new value.
 *
 */
const useInternalToolsMenu = () => {
  const [internalToolsMenuStorage, setInternalToolsMenuStorage] =
    useLocalStorage<InternalToolsMenu | null>({
      key: 'givebacks-internal-tools-menu',
      defaultValue: null,
      getInitialValueInEffect: false,
    });

  // Local state of the internal tools menu
  const [internalToolsMenuLocal, setInternalToolsMenuLocal] = useState<InternalToolsMenu | null>(
    null
  );

  /**
   * Set the value of a tool in the local storage
   */
  const setToolValue = useCallback(
    (key: keyof InternalToolsMenu, value: boolean) => {
      setInternalToolsMenuStorage((prev) => {
        prev ??= DEFAULT_INTERNAL_TOOLS_MENU;
        return { ...prev, [key]: { ...prev[key], value } };
      });
    },
    [setInternalToolsMenuStorage]
  );

  /**
   * Get the value of a tool in the local state
   */
  const getToolValue = useCallback(
    (key: keyof InternalToolsMenu) => internalToolsMenuLocal?.[key]?.value,
    [internalToolsMenuLocal]
  );
  /**
   * Resets the local storage to the default values
   */
  const resetMenu = useCallback(() => {
    setInternalToolsMenuStorage(DEFAULT_INTERNAL_TOOLS_MENU);
  }, [setInternalToolsMenuStorage]);

  /**
   * Returns the internal tools menu.
   * Will combine the local state with the default tools, this allows
   * new tools to be added to the menu without losing the previously saved values.
   * When it's undefined, return null.
   * When it's empty, return null.
   * Otherwise, return the internal tools menu.
   */
  const internalToolList = useMemo(() => {
    if (!internalToolsMenuLocal) {
      return null;
    }
    // convert the internal tools menu local to an array of [key, value] pairs
    // and filter out the tools that are not in the default tools
    const tools = Object.entries(internalToolsMenuLocal).filter(([key]) =>
      Object.keys(DEFAULT_INTERNAL_TOOLS_MENU).includes(key)
    ) as [keyof InternalToolsMenu, InternalToolStorage][];

    if (tools.length === 0) {
      return null;
    }

    // Filter out the tools that are already in the local state
    const defaultTools = Object.entries(DEFAULT_INTERNAL_TOOLS_MENU).filter(
      ([key]) => !tools.some(([toolKey]) => toolKey === key)
    ) as [keyof InternalToolsMenu, InternalToolStorage][];

    // Add the default tools to the tools list
    const toolsWithDefault = [...tools, ...defaultTools];

    const toolsWithOnChange = toolsWithDefault.map(([key, toolValue]) => ({
      key,
      ...toolValue,
      onChange: (value: boolean) => setToolValue(key, value),
      checked: toolValue.value,
    }));
    return toolsWithOnChange;
  }, [internalToolsMenuLocal, setToolValue]);

  const isToolsLoading = useMemo(() => {
    if (import.meta.env.VITE_API_ENV === 'production') {
      return false;
    }

    // Return is loading anytime the internal tool list is empty
    return Object.keys(internalToolList ?? {}).length == 0;
  }, [internalToolList]);

  /**
   * Sync local storage into local state when storage value is available or changes.
   * If local storage is not set, set local state to the default values.
   */
  useEffect(() => {
    if (internalToolsMenuStorage) {
      setInternalToolsMenuLocal(internalToolsMenuStorage);
    } else {
      setInternalToolsMenuLocal(DEFAULT_INTERNAL_TOOLS_MENU);
    }
  }, [internalToolsMenuStorage]);

  return {
    isToolsLoading,
    internalToolList,
    getToolValue,
    resetMenu,
  };
};

export { useInternalToolsMenu };
